// @flow
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import styled from '@emotion/styled';
import { color, FONT_WEIGHTS, SPACING, typography } from '@tablekit/theme';
import { ifRtl, margin } from '@tablekit/utils';
import Icon from '@tablekit/icon';
import LazyLoad from 'react-lazyload';
import type {
  BlokComponentPropsType,
  CardSimpleContentType
} from 'components/types';
import CoreImage from 'components/Image';
import Link, { isValidLink } from 'components/Link';
import { LAZY_LOAD_OFFSET } from 'utils/theme';
import { gridSpan } from 'styled/cssGrid';
import { Card as BaseCard } from '../styled';
import type { CardPropsType } from '../types';

export const SimpleContentCardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;

  @media (min-width: 576px) {
    margin: 0 -${SPACING.L3};
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
  }
`;

export const SMALL_IMAGE_SIZE = 95;
export const LARGE_IMAGE_SIZE = 176;

const StaticCard = styled(BaseCard.withComponent(Link))`
  width: 100%;
  margin: 0 0 ${SPACING.L5};

  @media (min-width: 1024px) {
    width: calc(50% - ${SPACING.L3} - ${SPACING.L3});
    margin: 0 ${parseFloat(SPACING.L5) / 2}px ${SPACING.L5};
  }
`;

export const BaseCardGrid = styled.div`
  display: -ms-grid;
  display: grid;
  flex: 1 1 auto;
  margin: ${SPACING.L4};
`;

const CardGrid = styled(BaseCardGrid)`
  -ms-grid-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L4} 1fr;
  grid-template-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L4} 1fr;

  @media (min-width: 768px) {
    -ms-grid-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L6} 1fr 1fr;
    grid-template-columns: ${SMALL_IMAGE_SIZE}px ${SPACING.L6} 1fr 1fr;
  }

  @media (min-width: 1280px) {
    -ms-grid-columns: ${LARGE_IMAGE_SIZE}px ${SPACING.L6} 1fr 1fr;
    grid-template-columns: ${LARGE_IMAGE_SIZE}px ${SPACING.L6} 1fr 1fr;
  }
`;

const SimpleContentBase = styled.div`
  display: flex;
  align-self: flex-start;
  text-align: ${ifRtl('right', 'left')};
`;

export const BaseSimpleContentTitle = styled(SimpleContentBase)`
  ${typography('L4')};
  font-weight: ${FONT_WEIGHTS.BOLD};
  color: ${color('primary')};
  align-self: center;
`;
const SimpleContentTitle = styled(BaseSimpleContentTitle)`
  ${gridSpan('column', 3, 1)};
  ${gridSpan('row', 1, 1)};
`;

export const BaseSimpleContentBody = styled(SimpleContentBase)`
  ${typography('L3')};
  align-items: flex-start;
  margin: ${SPACING.L2} 0;
`;
const SimpleContentBody = styled(SimpleContentBase)`
  ${gridSpan('column', 1, 3)};
  ${gridSpan('row', 2, 1)};
  min-height: 120px;

  @media (min-width: 768px) {
    ${gridSpan('column', 3, 2)};
    min-height: 96px;
  }
`;

export const BaseImagePlacement = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ImagePlacement = styled(BaseImagePlacement)`
  ${gridSpan('column', 1, 1)};
  ${gridSpan('row', 1, 1)};

  @media (min-width: 768px) {
    ${gridSpan('row', 1, 2)};
  }
`;

export const SimpleContentImage = styled(
  SimpleContentBase.withComponent(CoreImage)
)`
  max-width: ${SMALL_IMAGE_SIZE}px;
  max-height: ${SMALL_IMAGE_SIZE}px;
  align-self: center;

  @media (min-width: 1280px) {
    max-width: ${LARGE_IMAGE_SIZE}px;
    max-height: ${LARGE_IMAGE_SIZE}px;
  }
`;

export const BaseSimpleContentArrow = styled.div`
  height: ${(props) => typography('L4')(props).lineHeight};
  ${margin({ left: SPACING.L3 })};
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  justify-content: flex-end;
  color: ${color('primary')};
`;
export const SimpleContentArrow = styled(BaseSimpleContentArrow)`
  ${gridSpan('column', 4, 1)};
  ${gridSpan('row', 3, 1)};

  @media (min-width: 768px) {
    ${gridSpan('column', 4, 1)};
    ${gridSpan('row', 1, 2)};
  }
`;

export function CardSimpleContent({
  blok: { body, image, link, link_text: linkText, title },
  ...others
}: BlokComponentPropsType<CardSimpleContentType>) {
  const cardProps: CardPropsType = {
    ...others
  };
  let icon;
  if (isValidLink(link)) {
    cardProps.link = link;
    icon = (
      <SimpleContentArrow>
        <Icon icon={faArrowRight} />
      </SimpleContentArrow>
    );
  }
  return (
    <StaticCard {...cardProps}>
      <CardGrid>
        <ImagePlacement>
          {image && (
            <LazyLoad height={SMALL_IMAGE_SIZE} once offset={LAZY_LOAD_OFFSET}>
              <SimpleContentImage
                src={image}
                alt={title || linkText || body || image}
              />
            </LazyLoad>
          )}
        </ImagePlacement>
        <SimpleContentTitle>{title}</SimpleContentTitle>
        {icon}
        <SimpleContentBody>
          <div>{body}</div>
        </SimpleContentBody>
      </CardGrid>
    </StaticCard>
  );
}
